// src/mealData.js

const cuisines = [
  {
    name: "Cantonese Modern",
    style: "清蒸 (Qing Zheng) + Brown Butter Dashi",
    why: "Gentle steaming preserves moisture, while finishing with a controlled emulsion adds depth without masking the fish.",
    chefSecret: "Bloom aromatics at 200°C for 20 seconds to unlock volatile oils without scorching."      
  },
  {
    name: "Japanese Kaiseki",
    style: "西京焼き (Saikyo-yaki) + Maple Miso Glaze",
    why: "Miso curing denatures surface proteins and concentrates umami, improving browning and tenderness.",
    chefSecret: "Wipe excess miso before roasting to avoid bitter caramelization."
  },
  {
    name: "Korean Royal Court",
    style: "조림 (Jorim) + Gochujang-Mirin Lacquer",
    why: "Controlled reduction builds viscosity for a mirror glaze while avoiding toughening the protein.",
    chefSecret: "Reduce sauce to nappe consistency so it clings without pooling."
  },
  {
    name: "Thai Coastal",
    style: "นึ่งมะนาว (Nueng Manao) + Lemongrass Emulsion",
    why: "Add acid at the end to preserve brightness and prevent protein tightening.",
    chefSecret: "Use cilantro roots for deeper base aroma."
  },
  {
    name: "Modern American",
    style: "Sear + Pan-Roast with Green Garlic Oil",
    why: "A hot sear builds Maillard complexity; a lower finish temp maintains a custard-like interior.", 
    chefSecret: "Baste with foamy butter only after sear to avoid burning milk solids."
  }
];

const proteins = [
  { name: "Simply Nature Organic Atlantic Salmon", temp: "50-52°C", saltPct: 1.2, weightG: 220, cost: 6.00, time: "20-25 min" },
  { name: "Simply Nature Organic Cod Fillet", temp: "52-54°C", saltPct: 1.0, weightG: 220, cost: 5.00, time: "25-30 min" },
  { name: "Simply Nature Organic Tilapia", temp: "55-58°C", saltPct: 1.5, weightG: 220, cost: 4.50, time: "18-22 min" },
  { name: "Simply Nature Organic Large Eggs", temp: "63°C (Custard)", saltPct: 0.8, weightG: 180, cost: 1.80, time: "45-50 min" },
  { name: "Simply Nature Organic Firm Tofu", temp: "75°C", saltPct: 1.2, weightG: 240, cost: 1.10, time: "18-22 min" }
];

const seasonalProduce = {
  winter: { veggies: ["Russet Potatoes", "Carrots"], fruit: "Asian Pear" },
  spring: { veggies: ["Spinach", "Asparagus"], fruit: "Strawberries" },
  summer: { veggies: ["Sweet Corn", "Tomatoes"], fruit: "Watermelon" },
  fall: { veggies: ["Napa Cabbage", "Sweet Potatoes"], fruit: "Apples" }
};

export const getSeason = (month) => {
  if (month <= 1 || month === 11) return "winter";
  if (month <= 4) return "spring";
  if (month <= 7) return "summer";
  return "fall";
};

const grams = (n) => `${n}g`;
const mls = (n) => `${n}ml`;

export const getRecipeForDate = (date) => {
  if (!date) return null;
  const dayOfYear = Math.floor((date - new Date(date.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));      
  const month = date.getMonth();
  const seasonKey = getSeason(month);
  const season = seasonalProduce[seasonKey];

  const cuisine = cuisines[dayOfYear % cuisines.length];
  const protein = proteins[dayOfYear % proteins.length];
  const veggie = season.veggies[dayOfYear % season.veggies.length];

  const saltG = Math.round((protein.weightG * protein.saltPct) / 100);

  return {
    id: dayOfYear,
    name: `${cuisine.name}: ${cuisine.style} ${protein.name}`,
    level: "Michelin Executive Standard",
    cuisine: cuisine.name,
    technique: cuisine.style,
    prepTime: "18 min",
    cookTime: protein.time,
    servings: "1 Person (OMAD Optimized)",
    ingredients: {
      protein: [
        `${grams(protein.weightG)} ${protein.name} (center-cut)`,
        `${grams(saltG)} Iodized Sea Salt`,
        `${grams(0.6)} White Pepper`
      ],
      base: [
        `${grams(500)} Simply Nature Organic ${veggie}, batonnet`,
        `${grams(20)} Simply Nature Organic Ginger`,
        `${grams(10)} Simply Nature Organic Garlic`,
        `${grams(120)} Simply Nature Organic ${season.fruit}`
      ],
      finish: [
        `${mls(25)} Simply Nature Organic Extra Virgin Olive Oil`,
        `${mls(12)} Simply Nature Organic Soy Sauce`,
        `${mls(8)} Simply Nature Organic Rice Vinegar`,
        `${grams(6)} Simply Nature Organic Sugar`
      ]
    },
    instructions: [
      {
        step: "Pre-Cure",
        text: `Apply ${grams(saltG)} salt to ${protein.name}. Rest 15 minutes uncovered.`,
        why: `Salt briefly tightens surface proteins for cleaner sear and more even seasoning.`
      },
      {
        step: "Base Prep (Automated)",
        text: `Pulse ${grams(500)} ${veggie} with ${grams(20)} ginger and ${grams(10)} garlic in a FOOD PROCESSOR for 10 seconds.`,
        why: `Uniform particle size cooks evenly and releases aromatics without bruising.`
      },
      {
        step: "Main Execution",
        text: `Steam or poach to internal ${protein.temp} using a probe thermometer.`,
        why: `Precise internal temp avoids albumin weep and preserves a silky texture.`
      },
      {
        step: "Finish",
        text: `Gently warm ${mls(25)} EVOO to 50°C (do not sear), then pour over aromatics and finish with soy/vinegar.`,
        why: `Preserves delicate polyphenols in EVOO while still releasing aromatic oils.`
      }
    ],
    chefSecret: cuisine.chefSecret,
    nutrients: {
      calories: 2000 + (dayOfYear % 50),
      protein: "45-55g",
      fat: "30-35g",
      carbs: "10-15g"
    },
    cost: protein.cost + 2.75
  };
};

export const generateShoppingList = (startDate) => {
  const list = {};
  for (let i = 0; i < 14; i++) {
    const d = new Date(startDate);
    d.setDate(d.getDate() + i);
    const recipe = getRecipeForDate(d);
    const allIngs = [
      ...recipe.ingredients.protein,
      ...recipe.ingredients.base,
      ...recipe.ingredients.finish
    ];

    allIngs.forEach((ing) => {
      const match = ing.match(/(\d+(\.\d+)?)\s?(g|ml|kg|cans|units|dozen)\s(.+)/);
      if (match) {
        const name = match[4];
        const amt = parseFloat(match[1]) || 1;
        const unit = match[3] || "units";
        if (!list[name]) list[name] = { qty: 0, unit };
        list[name].qty += amt;
      }
    });
  }
  return Object.keys(list).map((name) => ({
    item: name,
    qty: `${Math.round(list[name].qty)}${list[name].unit}`,
    price: (list[name].qty / 100) * 1.0
  })).slice(0, 15);
};